<template>
  <div class="trial-page">
    <div class="trial-card">
      <!-- Banner 圖片 -->
      <img
        class="trial-banner"
        :src="trialBanner"
        alt="TSK 試用計劃 Banner"
      />

      <div class="trial-inner">
        <div class="trial-content">
          <!-- 標題 -->
          <h1 class="trial-title">
            {{ $t('trialModal.title') }}
          </h1>

          <!-- 說明文字 -->
          <div class="trial-text">
            <p class="trial-text-title">
              {{ $t('trialModal.title1') }}
            </p>
            <p class="trial-text-body">
              {{ $t('trialModal.body1') }}
            </p>

            <p class="trial-text-title">
              {{ $t('trialModal.title2') }}
            </p>
            <p class="trial-text-body">
              {{ $t('trialModal.body2') }}
            </p>
          </div>

          <!-- 尖針 -->
          <section class="trial-section">
            <div class="section-header">
              {{ $t('trialModal.sharp') }}
            </div>

            <div class="pick-row">
              <div class="pick-field">
                <span class="pick-label">{{ $t('trialModal.FirstPick') }}</span>
                <select v-model="form.sharpPick1">
                  <option value="">{{ $t('trialModal.choose') }}</option>
                  <option v-for="opt in sharpList1" :key="'s1-' + opt" :value="opt">
                    {{ opt }}
                  </option>
                </select>
              </div>

              <div class="pick-field">
                <span class="pick-label">{{ $t('trialModal.SecondPick') }}</span>
                <select v-model="form.sharpPick2">
                  <option value="">{{ $t('trialModal.choose') }}</option>
                  <option v-for="opt in sharpList2" :key="'s2-' + opt" :value="opt">
                    {{ opt }}
                  </option>
                </select>
              </div>

              <div class="pick-field">
                <span class="pick-label">{{ $t('trialModal.ThirdPick') }}</span>
                <select v-model="form.sharpPick3">
                  <option value="">{{ $t('trialModal.choose') }}</option>
                  <option v-for="opt in sharpList3" :key="'s3-' + opt" :value="opt">
                    {{ opt }}
                  </option>
                </select>
              </div>
            </div>
          </section>

          <!-- 鈍針 -->
          <section class="trial-section">
            <div class="section-header">
              {{ $t('trialModal.cannula') }}
            </div>

            <div class="pick-row">
              <div class="pick-field">
                <span class="pick-label">{{ $t('trialModal.FirstPick') }}</span>
                <select v-model="form.cannulaPick1">
                  <option value="">{{ $t('trialModal.choose') }}</option>
                  <option v-for="opt in cannulaList1" :key="'c1-' + opt" :value="opt">
                    {{ opt }}
                  </option>
                </select>
              </div>

              <div class="pick-field">
                <span class="pick-label">{{ $t('trialModal.SecondPick') }}</span>
                <select v-model="form.cannulaPick2">
                  <option value="">{{ $t('trialModal.choose') }}</option>
                  <option v-for="opt in cannulaList2" :key="'c2-' + opt" :value="opt">
                    {{ opt }}
                  </option>
                </select>
              </div>

              <div class="pick-field">
                <span class="pick-label">{{ $t('trialModal.ThirdPick') }}</span>
                <select v-model="form.cannulaPick3">
                  <option value="">{{ $t('trialModal.choose') }}</option>
                  <option v-for="opt in cannulaList3" :key="'c3-' + opt" :value="opt">
                    {{ opt }}
                  </option>
                </select>
              </div>
            </div>
          </section>

          <!-- 表單 -->
          <form class="trial-form" @submit.prevent="onSubmit">
            <!-- 姓名 -->
            <label class="field">
              <span class="field-label">
                {{ $t('trialModal.Recipient') }}<span class="req">*</span>
              </span>
              <input
                type="text"
                v-model.trim="form.recipient"
                :class="{ invalid: touched && errors.recipient }"
              />
              <small v-if="touched && errors.recipient" class="field-error">
                請輸入收件人姓名
              </small>
            </label>

            <!-- 醫院/診所/機構/醫師名稱 -->
            <label class="field">
              <span class="field-label">
                {{ $t('trialModal.organizationName') }}<span class="req">*</span>
              </span>
              <input
                type="text"
                v-model.trim="form.organizationName"
                :class="{ invalid: touched && errors.organizationName }"
              />
              <small v-if="touched && errors.organizationName" class="field-error">
                {{ $t('trialModal.organizationNameRequired') }}
              </small>
            </label>


            <!-- 電話 -->
            <label class="field">
              <span class="field-label">
                {{ $t('trialModal.RecipientPhone') }}<span class="req">*</span>
              </span>
              <input
                type="tel"
                v-model.trim="form.phone"
                :class="{ invalid: touched && errors.phone }"
              />
              <small v-if="touched && errors.phone" class="field-error">
                請輸入聯絡電話
              </small>
            </label>

            <!-- 地址 -->
            <div class="address-block">
              <span class="field-label">
                {{ $t('trialModal.RecipientAddress') }}<span class="req">*</span>
              </span>

              <div class="address-select-row">
                <select
                  v-model="addr.city"
                  @change="onCityChange"
                  :class="{ invalid: touched && errors.address }"
                >
                  <option value="" disabled>選擇縣市</option>
                  <option v-for="c in cities" :key="c" :value="c">{{ c }}</option>
                </select>

                <select
                  v-model="addr.district"
                  :disabled="!addr.city"
                  @change="onDistrictChange"
                  :class="{ invalid: touched && errors.address }"
                >
                  <option value="" disabled>選擇區域</option>
                  <option v-for="d in districtsOfSelectedCity" :key="d.name" :value="d.name">
                    {{ d.name }}
                  </option>
                </select>

                <input class="zip-input" :value="addr.zip" readonly />
              </div>

              <input
                type="text"
                class="address-detail"
                v-model.trim="addr.detail"
                placeholder="請輸入路名、門牌…"
                :class="{ invalid: touched && errors.address }"
              />

              <small v-if="touched && errors.address" class="field-error">
                請完整填寫縣市、區域與詳細地址
              </small>

              <small class="address-hint" v-if="composedAddress">
                {{ composedAddress }}
              </small>
            </div>

            <!-- 備註 -->
            <label class="field">
              <span class="field-label">{{ $t('trialModal.Remarks') }}</span>
              <textarea rows="4" v-model="form.remark"></textarea>
            </label>

            <!-- 注意事項 -->
            <p class="trial-tip" v-html="$t('trialModal.tip')"></p>

            <!-- 訊息 -->
            <p v-if="successMessage" class="msg success">{{ successMessage }}</p>
            <p v-if="errorMessage" class="msg error">{{ errorMessage }}</p>

            <!-- 送出 -->
            <div class="submit-row">
              <button class="btn-submit" :disabled="submitting">
                {{ submitting ? '送出中…' : $t('trialModal.sand') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from '@/lib/supabase'
import trialBannerImg from '@/assets/images/AppShell/Trial.jpg'
import { SHARP_OPTIONS, CANNULA_OPTIONS } from '@/config/trialOptions'
import districtsData from '@/lib/taiwan_districts.json'

export default {
  name: 'AppTrial',
  data () {
    return {
      trialBanner: trialBannerImg,
      sharpOptions: SHARP_OPTIONS,
      cannulaOptions: CANNULA_OPTIONS,
      form: {
        recipient: '',
        organizationName: '',
        phone: '',
        address: '',          // 這行現在其實已經不用了，保留也沒關係
        remark: '',
        sharpPick1: '',
        sharpPick2: '',
        sharpPick3: '',
        cannulaPick1: '',
        cannulaPick2: '',
        cannulaPick3: ''
      },
      submitting: false,
      successMessage: '',
      errorMessage: '',
      // 地址模組
      addr: {
        city: '',
        district: '',
        zip: '',
        detail: ''
      },
      cities: districtsData.map(c => c.name),
      touched: false,
      errors: {
        recipient: false,
        organizationName: false,
        phone: false,
        address: false
      }
    }
  },
  computed: {
    // 尖針 三個順位可選清單（自動排除前面選過的）
    sharpList1 () {
      return this.sharpOptions
    },
    sharpList2 () {
      return this.sharpOptions.filter(o => o !== this.form.sharpPick1)
    },
    sharpList3 () {
      return this.sharpOptions.filter(
        o => o !== this.form.sharpPick1 && o !== this.form.sharpPick2
      )
    },

    // 鈍針 三個順位可選清單
    cannulaList1 () {
      return this.cannulaOptions
    },
    cannulaList2 () {
      return this.cannulaOptions.filter(o => o !== this.form.cannulaPick1)
    },
    cannulaList3 () {
      return this.cannulaOptions.filter(
        o => o !== this.form.cannulaPick1 && o !== this.form.cannulaPick2
      )
    },

    // 地址：目前選到的縣市的區列表
    districtsOfSelectedCity () {
      if (!this.addr.city) return []
      const found = districtsData.find(c => c.name === this.addr.city)
      return found ? found.districts : []
    },

    // 地址：組合成一條字串，送進 DB 的值
    composedAddress () {
      const parts = [
        (this.addr.zip || '').trim(),
        (this.addr.city || '').trim(),
        (this.addr.district || '').trim(),
        (this.addr.detail || '').trim()
      ].filter(Boolean)
      return parts.join(' ')
    },

    canSubmit () {
      const hasName = (this.form.recipient || '').trim().length > 0
      const hasPhone = (this.form.phone || '').trim().length > 0

      const hasCity = (this.addr.city || '').trim().length > 0
      const hasDistrict = (this.addr.district || '').trim().length > 0
      const hasDetail = (this.addr.detail || '').trim().length > 0

      return hasName && hasPhone && hasCity && hasDistrict && hasDetail && !this.submitting
    }
  },
  methods: {
    onCityChange () {
      this.addr.district = ''
      this.addr.zip = ''
    },

    onDistrictChange () {
      const found = this.districtsOfSelectedCity.find(
        d => d.name === this.addr.district
      )
      this.addr.zip = found ? found.zip : ''
    },

    // ✅ 唯一的 onSubmit（使用 composedAddress）
    async onSubmit () {
      if (this.submitting) return

      this.touched = true
      this.successMessage = ''
      this.errorMessage = ''

      if (!this.validateForm()) {
        this.errorMessage = '請填寫所有必填欄位'
        return
      }

      const name = (this.form.recipient || '').trim()
      const org = (this.form.organizationName || '').trim()
      const phone = (this.form.phone || '').trim()
      const city = (this.addr.city || '').trim()
      const district = (this.addr.district || '').trim()
      const detail = (this.addr.detail || '').trim()

      if (!name) {
        this.errorMessage = '請輸入收件人姓名'
        return
      }
      if (!org) {
        this.errorMessage = this.$t('trialModal.organizationNameRequired')
        return
      }
      if (!phone) {
        this.errorMessage = '請輸入聯絡手機'
        return
      }
      if (!city || !district || !detail) {
        this.errorMessage = '請完整填寫寄送地址'
        return
      }


      try {
        this.submitting = true

        const { data: sess } = await supabase.auth.getSession()
        const userId = sess?.session?.user?.id || null

        const { error } = await supabase
          .from('trial_requests')
          .insert([
            {
              user_id: userId,
              recipient_name: this.form.recipient,
              organization_name: this.form.organizationName,
              recipient_phone: this.form.phone,
              recipient_address: this.composedAddress, // ★ 寫入完整地址
              remark: this.form.remark,

              sharp_pick1: this.form.sharpPick1 || null,
              sharp_pick2: this.form.sharpPick2 || null,
              sharp_pick3: this.form.sharpPick3 || null,
              cannula_pick1: this.form.cannulaPick1 || null,
              cannula_pick2: this.form.cannulaPick2 || null,
              cannula_pick3: this.form.cannulaPick3 || null
            }
          ])

        if (error) throw error

        this.successMessage = '您的索取試用已成功送出，我們將儘快與您聯繫。'

        // 清空表單
        this.form.recipient = ''
        this.form.organizationName = ''
        this.form.phone = ''
        this.form.remark = ''
        this.form.sharpPick1 = ''
        this.form.sharpPick2 = ''
        this.form.sharpPick3 = ''
        this.form.cannulaPick1 = ''
        this.form.cannulaPick2 = ''
        this.form.cannulaPick3 = ''
        this.addr = { city: '', district: '', zip: '', detail: '' }
      } catch (e) {
        console.error('submit failed:', e)
        this.errorMessage = '送出失敗，請稍後再試。'
      } finally {
        this.submitting = false
      }
    },
    validateForm () {
      const name = (this.form.recipient || '').trim()
      const org = (this.form.organizationName || '').trim()
      const phone = (this.form.phone || '').trim()
      const city = (this.addr.city || '').trim()
      const district = (this.addr.district || '').trim()
      const detail = (this.addr.detail || '').trim()

      this.errors.recipient = !name
      this.errors.organizationName = !org
      this.errors.phone = !phone
      this.errors.address = !city || !district || !detail

      return !this.errors.organizationName && !this.errors.recipient && !this.errors.phone && !this.errors.address
    },
  }
}
</script>

<style scoped>
.trial-page {
  max-width: 960px;
  margin: 0 auto;
  padding: 24px 0 40px;
}

/* 白底容器 */
.trial-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #eee;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.trial-banner {
  display: block;
  width: 100%;
}

/* 外層：給整塊一點 padding，並把內容置中 */
.trial-inner {
  padding: 28px 32px 32px;
  display: flex;
  justify-content: center;
}

/* 內層：實際內容寬度（窄一點） */
.trial-content {
  width: 100%;
  max-width: 640px;
}

@media (max-width: 640px) {
  .trial-inner {
    padding: 20px 16px 24px;
  }
  .trial-content {
    max-width: 100%;
  }
}

/* 標題仍置中 */
.trial-title {
  text-align: center;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 18px;
}

/* 說明文字：整塊靠左 */
.trial-text {
  font-size: 14px;
  color: #444;
  line-height: 1.7;
  margin-bottom: 20px;
  text-align: left;
}

.trial-text-title {
  font-weight: 700;
  margin-bottom: 4px;
  text-align: left;
}

.trial-text-body {
  margin-bottom: 12px;
  text-align: left;
}

/* TSK 選擇區 */
.trial-section {
  margin-bottom: 30px;
}

.trial-section + .trial-section {
  margin-top: 70px; /* 讓第二塊（鈍針區）與上面多一點距離 */
}


.section-header {
  background: #111;
  color: #fff;
  padding: 10px;
  border-radius: 999px;
  text-align: center;
  font-size: 14px;
  margin-bottom: 12px;
}

.pick-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

/* 每一個下拉的容器 */
.pick-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pick-label {
  font-size: 12px;
  color: #555;
}

/* 把 select 做成接近原本 pill 的感覺 */
.pick-field select {
  width: 100%;
  border-radius: 999px;
  border: 1px solid #ddd;
  padding: 8px 10px;
  font-size: 13px;
  background: #fff;
  appearance: none;
  -moz-appearance: none;
  -webkit-appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, #aaa 50%),
    linear-gradient(135deg, #aaa 50%, transparent 50%);
  background-position: calc(100% - 14px) 50%, calc(100% - 9px) 50%;
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
}

/* 表單 */
.trial-form {
  display: grid;
  gap: 14px;
  margin-top: 6px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.field-label {
  font-size: 14px;
  font-weight: 600;
  color: #444;
  text-align: left;
}

input,
textarea {
  border: 1px solid #ddd;
  border-radius: 10px;
  padding: 10px;
  font-size: 14px;
}

/* 注意事項：靠左 */
.trial-tip {
  margin-top: 10px;
  color: #777;
  font-size: 12px;
  line-height: 1.7;
  text-align: left;
}

/* 訊息 */
.msg {
  font-size: 13px;
}
.msg.success {
  color: #0a7a3d;
}
.msg.error {
  color: #c0392b;
}

/* 送出按鈕 */
.submit-row {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.btn-submit {
  background: #111;
  color: #fff;
  padding: 10px 18px;
  border-radius: 999px;
  border: 0;
  font-size: 14px;
  cursor: pointer;
}

.btn-submit:disabled {
  opacity: 0.6;
}
.address-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.address-select-row {
  display: grid;
  grid-template-columns: 1fr 1fr 110px;
  gap: 10px;
}

.address-select-row select,
.address-detail {
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #ddd;
  font-size: 14px;
}

.zip-input {
  background: #fafafa;
}

.address-hint {
  font-size: 12px;
  color: #777;
}
@media (max-width: 640px) {
  .address-select-row {
    grid-template-columns: 1fr;
  }
  .zip-input {
    width: 100%;
  }
}

.req {
  color: #e53935;
  margin-left: 4px;
  font-weight: 700;
}

.invalid {
  border-color: #e53935 !important;
  background: #fff6f6;
}

.field-error {
  color: #e53935;
  font-size: 12px;
  margin-top: 2px;
}


</style>
